import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { pedidosService } from '@/api/pedidos';
import { useEmpresaStore } from '@/store/empresaStore';
import Layout from '@/components/layout/Layout';
import LoadingSpinner from '@/components/common/LoadingSpinner';

export default function PedidoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const empresaSeleccionada = useEmpresaStore(state => state.empresaSeleccionada);

  const { data: pedido, isLoading } = useQuery({
    queryKey: ['pedido', id, empresaSeleccionada?.id],
    queryFn: () => pedidosService.getPedidoById(id!, empresaSeleccionada!.id),
    enabled: !!id && !!empresaSeleccionada,
  });

  const getEstadoBadgeColor = (estado: number) => {
    const colors: Record<number, string> = {
      0: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200',
      2: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200',
    };
    return colors[estado] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-200';
  };

  const getEstadoLabel = (estado: number) => {
    const labels: Record<number, string> = {
      0: 'Pendiente',
      2: 'Servido',
    };
    return labels[estado] || 'Desconocido';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <Layout title="Detalle del Pedido" subtitle="Cargando informacion del pedido" showBack>
        <LoadingSpinner />
      </Layout>
    );
  }

  if (!pedido) {
    return (
      <Layout title="Detalle del Pedido" subtitle="No se ha encontrado el pedido solicitado" showBack>
        <div className="flex flex-col items-center justify-center min-h-[400px] p-8">
          <span className="material-symbols-outlined text-slate-400 text-6xl mb-4">error</span>
          <h3 className="text-xl font-bold mb-2">Pedido no encontrado</h3>
          <p className="text-slate-500 text-center mb-6">
            El pedido que buscas no existe o ha sido eliminado
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={`Pedido ${pedido.numero}`} subtitle="Revisa el detalle completo del pedido" showBack>
      <div className="flex-1 pb-24">
        {/* Header con Estado */}
        <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-2xl font-bold text-[#0d141b] dark:text-white">
              {pedido.numero}
            </h2>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getEstadoBadgeColor(pedido.estado)}`}>
              {getEstadoLabel(pedido.estado)}
            </span>
          </div>
          <p className="text-[#4c739a] dark:text-slate-400 text-sm">
            {formatDate(pedido.fecha)}
          </p>
        </div>

        {/* Información del Cliente */}
        <section className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 p-4">
          <h3 className="text-[#0d141b] dark:text-white font-bold mb-3 flex items-center gap-2">
            <span className="material-symbols-outlined">person</span>
            Cliente
          </h3>
          <p className="text-[#0d141b] dark:text-white font-medium">
            {pedido.clienteNombre || `Cliente ${pedido.clienteId}`}
          </p>
        </section>

        {/* Dirección de Entrega */}
        <section className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 p-4">
          <h3 className="text-[#0d141b] dark:text-white font-bold mb-3 flex items-center gap-2">
            <span className="material-symbols-outlined">local_shipping</span>
            Dirección de Entrega
          </h3>
          <p className="text-[#0d141b] dark:text-white">
            {pedido.direccionEntrega || 'No especificada'}
          </p>
        </section>

        {/* Observaciones */}
        {pedido.observaciones && (
          <section className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 p-4">
            <h3 className="text-[#0d141b] dark:text-white font-bold mb-3 flex items-center gap-2">
              <span className="material-symbols-outlined">description</span>
              Observaciones
            </h3>
            <p className="text-[#4c739a] dark:text-slate-400">
              {pedido.observaciones}
            </p>
          </section>
        )}

        {/* Líneas del Pedido */}
        <section className="p-4">
          <h3 className="text-[#0d141b] dark:text-white font-bold mb-3 flex items-center gap-2">
            <span className="material-symbols-outlined">inventory_2</span>
            Artículos ({pedido.lineas.length})
          </h3>
          <div className="space-y-3">
            {pedido.lineas.map((linea, index) => {
              const precioConDescuento = linea.precio * (1 - linea.descuento / 100);

              return (
                <div 
                  key={linea.articuloId || index} 
                  className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <p className="text-[#0d141b] dark:text-white font-semibold mb-1">
                        {linea.articuloNombre || `Artículo ${linea.articuloId}`}
                      </p>
                      <p className="text-xs text-[#4c739a] dark:text-slate-400 mb-2">
                        Cód. {linea.articuloCodigo}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-[#4c739a] dark:text-slate-400 mb-2">
                        <span>Cantidad: {linea.cantidad}</span>
                        {linea.descuento > 0 && (
                          <span className="text-green-600 dark:text-green-400 font-medium">
                            {linea.descuento}% desc.
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="text-sm">
                          {linea.descuento > 0 && (
                            <span className="text-[#4c739a] dark:text-slate-400 line-through mr-2">
                              {linea.precio.toFixed(2)}€
                            </span>
                          )}
                          <span className="text-[#0d141b] dark:text-white font-medium">
                            {precioConDescuento.toFixed(2)}€/ud
                          </span>
                        </div>
                        <p className="text-[#0d141b] dark:text-white font-bold">
                          {(linea.subtotal || 0).toFixed(2)}€
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Totales */}
        <section className="px-4 pb-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
            <div className="space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-[#4c739a] dark:text-slate-400">Subtotal</span>
                <span className="text-[#0d141b] dark:text-white font-medium">
                  {pedido.subtotal.toFixed(2)}€
                </span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-[#4c739a] dark:text-slate-400">IVA</span>
                <span className="text-[#0d141b] dark:text-white font-medium">
                  {pedido.totalIva.toFixed(2)}€
                </span>
              </div>
              <div className="pt-3 border-t border-slate-100 dark:border-slate-700 flex justify-between items-center">
                <span className="text-[#0d141b] dark:text-white font-bold text-lg">Total</span>
                <span className="text-primary font-bold text-xl">
                  {pedido.total.toFixed(2)}€
                </span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
